import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { medications } from '../services/api';
import { ToastContext } from '../App';

function MedicationList() {
  const [medicationList, setMedicationList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { showToast } = useContext(ToastContext) || {};

  useEffect(() => {
    fetchMedications();
  }, []);

  const fetchMedications = async () => {
    try {
      // Fetch both 'available' and 'review_ready' statuses for pharmacist review queue
      const [availableRes, reviewReadyRes] = await Promise.all([
        medications.list({ status: 'available' }),
        medications.list({ status: 'review_ready' })
      ]);
      // Combine both lists
      const combined = [...(availableRes.data || []), ...(reviewReadyRes.data || [])];
      setMedicationList(combined);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to load medications. Please try again.';
      
      if (showToast) {
        showToast(errorMessage, 'error');
      }
      
      // Handle timeout/network errors
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setError('Request timed out. Please check your connection and try again.');
      } else if (!err.response) {
        setError('Network error. Please check your internet connection.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async (id) => {
    try {
      await medications.claim(id);
      
      if (showToast) {
        showToast('Medication claimed successfully!', 'success');
      }
      
      // Refresh the medication list to show updated status
      await fetchMedications();
      
      // Navigate to review page
      navigate(`/dashboard/review/${id}`);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to claim medication. You may already have an active claim.';
      
      if (showToast) {
        showToast(errorMessage, 'error');
      }
      
      console.error('Error claiming medication:', err);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner large"></div>
        <p>Loading medications...</p>
      </div>
    );
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="medication-list">
      <h2>Available Medications</h2>
      <p className="subtitle">Select a medication to review</p>

      {medicationList.length === 0 ? (
        <div className="empty-state">
          <p>🎉 No medications available for review at the moment.</p>
          <p>Check back later for new content!</p>
        </div>
      ) : (
        <table className="medications-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Generic Name</th>
              <th>Drug Classes</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {medicationList.map((med) => (
              <tr key={med.id}>
                <td className="med-name">{med.name}</td>
                <td>{med.generic_name || 'N/A'}</td>
                <td>
                  {med.drug_classes && med.drug_classes.length > 0
                    ? med.drug_classes.join(', ')
                    : 'N/A'}
                </td>
                <td>
                  <span className={`status-badge status-${med.status}`}>
                    {med.status}
                  </span>
                </td>
                <td>
                  <button
                    onClick={() => handleClaim(med.id)}
                    className="btn-primary btn-small"
                  >
                    Claim for Review
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default MedicationList;
